import clsx from 'clsx';
import React, { useState } from 'react';
import { Control, Controller, UseFormRegister } from 'react-hook-form';

import FormGroupContainer from './FormGroupContainer/FormGroupContainer';
import SupplierForm from './SupplierForm/SupplierForm';

import FormWrapper from '../../../Elements/FormWrapper/FormWrapper';
import { useTreatments } from '../../../store/treatments.store';
import { RemoteOptions } from '../../../types';
import { AutomatedDraft, Draft } from '../../../types/draft';
import { FormTypeOption, TreatmentForm } from '../../../types/treatment';
import DateInputField from '../Fields/DateInputField/DateInputField';
import FieldWrapper from '../Fields/FieldWrapper/FieldWrapper';
import InputField from '../Fields/InputField/InputField';
import SelectField from '../Fields/SelectField/SelectField';
import SupplierField from '../Fields/SupplierField/SupplierField';
import TypeField from '../Fields/TypeField/TypeField';

type Form = {
  register: UseFormRegister<TreatmentForm>;
  control: Control<TreatmentForm>;
  dropdowns: RemoteOptions;
  disabledFields: FormTypeOption['disabledFields'];
  requiredFields: FormTypeOption['requiredFields'];
  useBankAccount?: boolean;
};

// Create a type guard to check if a form is of type AutomatedDraft
const checkIsAutomated = (draft: Draft | AutomatedDraft): draft is AutomatedDraft => {
  return !!draft?.automationId;
};

const Form = ({
  register,
  control,
  dropdowns,
  disabledFields,
  requiredFields,
  useBankAccount = false,
}: Form) => {
  const { isMethodDisabled, isAccountDisabled, isTermsDisabled, isDueOnDisabled } = disabledFields;
  const { isMethodRequired, isAccountRequired, isTermsRequired, isDueOnRequired } = requiredFields;
  const [showSupplierForm, setShowSupplierForm] = useState(false);

  const { draft } = useTreatments();

  const isAutomated = checkIsAutomated(draft);

  return (
    <FormWrapper>
      <FormGroupContainer
        className={clsx(
          'tw-w-full tw-flex tw-border-solid tw-border-b-[1px] tw-border-b-cfo-grey-500 tw-pb-[5px]'
        )}
      >
        <FormGroupContainer className={clsx('tw-max-w-[180px] tw-w-full', 'tw-mr-[15px]')}>
          <TypeField />
        </FormGroupContainer>

        <FormGroupContainer className={clsx('tw-w-full')}>
          <FieldWrapper
            label={'Payment Account'}
            required={isAccountRequired}
            labelWidthClasses={clsx('tw-min-w-[114px] tw-max-w-[114px]')}
            labelClassName={clsx('tw-text-cfo-grey-600', {
              'tw-text-opacity-50': isAccountDisabled,
            })}
          >
            {useBankAccount ? (
              <Controller
                name={'bankAccount'}
                control={control}
                rules={{ required: isAccountRequired }}
                render={({ field }) => (
                  <SelectField
                    isSearchable={true}
                    selected={field.value}
                    disabled={isAccountDisabled}
                    options={dropdowns.bankAccounts}
                    onChange={field.onChange}
                    classes={{ container: 'tw-w-full' }}
                    isAiGenerated={
                      isAutomated &&
                      draft.payload.bankAccountIsAutomated &&
                      !!draft.payload.bankAccount &&
                      field.value.publishData === draft.payload.bankAccount
                    }
                  />
                )}
              />
            ) : (
              <Controller
                name={'paymentAccount'}
                control={control}
                rules={{ required: isAccountRequired }}
                render={({ field }) => (
                  <SelectField
                    isSearchable={true}
                    selected={field.value}
                    disabled={isAccountDisabled}
                    options={dropdowns.paymentAccounts}
                    onChange={field.onChange}
                    classes={{ container: 'tw-w-full' }}
                    isAiGenerated={
                      isAutomated &&
                      draft.payload.paymentAccountIsAutomated &&
                      !!draft.payload.paymentAccount &&
                      field.value.publishData === draft.payload.paymentAccount
                    }
                  />
                )}
              />
            )}
          </FieldWrapper>

          <FieldWrapper
            label={'Payment method'}
            required={isMethodRequired}
            labelWidthClasses={clsx('tw-min-w-[114px] tw-max-w-[114px]')}
            labelClassName={clsx('tw-text-cfo-grey-600', {
              'tw-text-opacity-50': isMethodDisabled,
            })}
          >
            <Controller
              name={`paymentMethod`}
              control={control}
              rules={{ required: isMethodRequired }}
              render={({ field }) => (
                <SelectField
                  isSearchable={true}
                  selected={field.value}
                  disabled={isMethodDisabled}
                  options={dropdowns.paymentMethods}
                  onChange={field.onChange}
                  classes={{ container: 'tw-w-full' }}
                  isAiGenerated={
                    isAutomated &&
                    draft.payload.paymentMethodIsAutomated &&
                    !!draft.payload.paymentMethod &&
                    field.value.publishData === draft.payload.paymentMethod
                  }
                />
              )}
            />
          </FieldWrapper>
        </FormGroupContainer>
      </FormGroupContainer>

      {/* TO-DO: To update functionalities for ai-generated supplier */}
      {!showSupplierForm && (
        <SupplierField
          onCreate={() => setShowSupplierForm(true)}
          control={control}
          dropdowns={dropdowns}
        />
      )}

      {showSupplierForm && (
        <SupplierForm
          currencies={dropdowns.currencies}
          suppliers={dropdowns.payees}
          defaultCurrency={dropdowns.defaultCurrency}
          onClose={() => setShowSupplierForm(false)}
        />
      )}

      <FormGroupContainer className={clsx('tw-w-full tw-flex')}>
        <Controller
          name={`referenceNumber`}
          control={control}
          render={({ field }) => {
            return (
              <InputField
                label="Document ref"
                labelClassName={clsx('tw-text-cfo-grey-600')}
                labelWidthClasses={clsx('tw-min-w-[98px] tw-max-w-[98px]')}
                containerClassName={clsx('tw-min-w-[300px] tw-w-full tw-mr-[10px]')}
                placeholder="Document ref"
                type="text"
                autoComplete="off"
                registration={register(`referenceNumber`)}
                isAiGenerated={
                  isAutomated &&
                  draft.payload.referenceNumberIsAutomated &&
                  !!draft.payload.referenceNumber &&
                  field.value === draft.payload.referenceNumber
                }
              />
            );
          }}
        />
        <Controller
          name={`transactionDate`}
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <DateInputField
              label={`Date`}
              labelClassName={clsx('tw-text-cfo-grey-600')}
              labelWidthClasses={clsx('tw-min-w-[50px] tw-max-w-[50px]')}
              isRequired={true}
              value={field.value}
              onChange={field.onChange}
              isAiGenerated={
                isAutomated &&
                draft.payload.transactionDateIsAutomated &&
                !!draft.payload.transactionDate &&
                field.value === draft.payload.transactionDate
              }
            />
          )}
        />
      </FormGroupContainer>

      <FormGroupContainer className={clsx('tw-w-full tw-flex')}>
        <FieldWrapper
          label={'Terms'}
          labelClassName={clsx('tw-text-cfo-grey-600', {
            'tw-text-opacity-50': isTermsDisabled,
          })}
          labelWidthClasses={clsx('tw-min-w-[98px] tw-max-w-[98px]')}
          containerClassName={clsx('tw-min-w-[300px] tw-w-full tw-mr-[10px]')}
          required={isTermsRequired}
        >
          <Controller
            name={`salesTerm`}
            control={control}
            render={({ field }) => (
              <SelectField
                isSearchable={true}
                firstIsNull={true}
                selected={field.value}
                disabled={isTermsDisabled}
                options={dropdowns.terms}
                onChange={field.onChange}
                isAiGenerated={
                  isAutomated &&
                  draft.payload.salesTermIsAutomated &&
                  !!draft.payload.salesTerm &&
                  field.value.publishData === draft.payload.salesTerm
                }
              />
            )}
          />
        </FieldWrapper>

        <Controller
          name={`dueDate`}
          control={control}
          rules={{ required: isDueOnRequired }}
          render={({ field }) => (
            <DateInputField
              label={`Due on`}
              labelClassName={clsx('tw-text-cfo-grey-600', {
                'tw-text-opacity-50': isDueOnDisabled,
              })}
              labelWidthClasses={clsx('tw-min-w-[50px] tw-max-w-[50px]')}
              isRequired={isDueOnRequired}
              disabled={isDueOnDisabled}
              value={field.value}
              onChange={field.onChange}
              isAiGenerated={
                isAutomated &&
                draft.payload.dueDateIsAutomated &&
                !!draft.payload.dueDate &&
                field.value === draft.payload.dueDate
              }
            />
          )}
        />
      </FormGroupContainer>

      <FormGroupContainer className={clsx('tw-max-w-[300px] tw-w-full tw-flex')}>
        <FieldWrapper
          label={'Location'}
          labelClassName={clsx('tw-text-cfo-grey-600')}
          labelWidthClasses={clsx('tw-min-w-[98px] tw-max-w-[98px]')}
        >
          <Controller
            name={`location`}
            control={control}
            render={({ field }) => (
              <SelectField
                isSearchable={true}
                firstIsNull={true}
                selected={field.value}
                options={dropdowns.locations}
                onChange={field.onChange}
                isAiGenerated={
                  isAutomated &&
                  draft.payload.locationIsAutomated &&
                  !!draft.payload.location &&
                  field.value.publishData === draft.payload.location
                }
              />
            )}
          />
        </FieldWrapper>
      </FormGroupContainer>
    </FormWrapper>
  );
};

export default Form;
